/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 17/10/22
 */

import { WithCurrentAssignmentProps } from '@/components/contexts/AssignmentContext/withCurrentAssignment';
import { Dialog } from '@/components/Dialog';
import { LabeledSelect } from '@/components/FormInputField';
import config from '@/config';
import { Workplan, WorkplanState } from '@/wpt-lib/interfaces/Workplan';
import { usePostRequest } from '@/utils/useStatefulRequests';
import { Text } from '@chakra-ui/react';
import { range } from 'lodash';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useWorkplanVersion from '@/components/contexts/VersionContext/useWorkplanVersion';
import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';

const CopyWorkplanDialog = ({
    workplan,
    onClose,
    currentAssignment,
}: {
    workplan?: Workplan;
    onClose(): void;
} & WithCurrentAssignmentProps) => {
    const navigate = useNavigate();
    const { workplanVersion } = useWorkplanVersion();
    const { activeAssignments, setAssignment } = useAssignment();

    const [copyAssignment, setCopyAssignment] = useState(
        currentAssignment.inactive ? activeAssignments?.[0]?.id ?? currentAssignment.id : currentAssignment.id,
    );
    useEffect(() => {
        if (currentAssignment.inactive && currentAssignment.id === copyAssignment && activeAssignments?.length > 0) {
            setCopyAssignment(activeAssignments[0].id);
        }
    }, [activeAssignments, currentAssignment, copyAssignment]);

    const startYear = new Date().getFullYear();
    const prevYear = new Date().getFullYear() - 1; // to allow prev year workplan creation as well

    const makeYears = () =>
        range(
            prevYear,
            startYear + config.constants.plannableFutureYears + 1, // +1 for current year
        ).filter(
            (year) =>
                !(
                    currentAssignment.id === copyAssignment &&
                    workplanVersion?.state !== WorkplanState.HISTORICAL &&
                    year === workplan?.year
                ),
        );

    const defaultOption = (years: number[]) => years.find((y) => y > startYear) ?? years[0];

    const yearsToCreate = makeYears();
    const [year, setYear] = useState(defaultOption(yearsToCreate));

    const [copyErrorStr, setCopyErrorStr] = useState<string>();
    const { loading, makeRequest } = usePostRequest<Workplan, { year: number; copyIntoAssignmentId: number }>();

    const doCopy = async () => {
        if (workplan && workplanVersion && activeAssignments) {
            const assignment = activeAssignments.find((a) => a.id === copyAssignment);
            if (!assignment) {
                setCopyErrorStr(`Could not find assignment to copy workplan to. Please select a different assignment.`);
                return;
            }

            try {
                const response = await makeRequest(
                    `/workplan/${workplan?.id}/${workplanVersion.version}/copy`,
                    {
                        year,
                        copyIntoAssignmentId: copyAssignment,
                    },
                    {
                        params: {
                            assignment: currentAssignment.id,
                        },
                    },
                );

                setAssignment(assignment);
                navigate(`/workplan/${response.data.id}/${response.data.currentVersion.version}`);
                onClose();
            } catch (e) {
                setCopyErrorStr(
                    `Could not copy ${workplan?.year} workplan to ${year} workplan for assignment ${assignment.assignmentNumber}. ` +
                        `If the destination workplan is being reviewed or endorsed, it is not possible to copy to that workplan.`,
                );
            }
        }
    };

    useEffect(() => {
        setYear(defaultOption(makeYears()));
    }, [copyAssignment]);

    useEffect(() => {
        if (workplan) {
            setCopyErrorStr(undefined);
            setYear(defaultOption(makeYears()));
        }
    }, [workplan]);

    return (
        <Dialog
            isOpen={!!workplan}
            isLoading={loading}
            dialogHeader="Copy Workplan"
            dialogConfirmLabel="Copy Workplan"
            onClose={onClose}
            errorMsg={copyErrorStr}
            dialogAction={doCopy}
        >
            <>
                <Text>Select the assignment and year you want to copy the contents of this workplan into.</Text>
                <Text fontStyle="italic">
                    Note: this will copy all existing content and the workload allocation percentages from the existing
                    workplan into the new workplan depending on the assignment and year that is selected.
                </Text>
                {activeAssignments && (
                    <LabeledSelect
                        label="Select assignment:"
                        value={copyAssignment}
                        onChange={(e) => setCopyAssignment(+e.target.value)}
                        isDisabled={loading}
                    >
                        {activeAssignments.map((assignment) => (
                            <option key={assignment.id} value={assignment.id}>
                                {`${assignment.assignmentNumber} - ${assignment.name}`}
                            </option>
                        ))}
                    </LabeledSelect>
                )}
                <LabeledSelect
                    label="Select year:"
                    value={year}
                    onChange={(e) => setYear(+e.target.value)}
                    isDisabled={loading}
                >
                    {yearsToCreate.map((y) => (
                        <option key={y} value={y}>
                            {y}
                        </option>
                    ))}
                </LabeledSelect>
            </>
        </Dialog>
    );
};

export default CopyWorkplanDialog;
